import React, { FC, useState, useEffect, useRef } from 'react';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { LineChart, ReferenceLineProps } from '@thingslog/ui-components';
import {
  ResponsiveContainerProps,
  LineProps,
  CartesianGridProps,
  TooltipProps,
  XAxisProps,
  YAxisProps,
} from '@thingslog/ui-components';

const InteractiveLineChart: FC<InteractiveLineChartProps> = (props: InteractiveLineChartProps) => {
  const [domain, setDomain] = useState<number[]>([0, props.lineChart.data.length - 1]);
  const [startX, setStartX] = useState<number | null>(null);
  const chartWrapperRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (chartWrapperRef && chartWrapperRef.current) {
      chartWrapperRef.current.addEventListener('wheel', (e: Event): void => e.preventDefault());
    }
  }, []);

  useEffect(() => {
    setDomain([0, props.lineChart.data.length - 1]);
  }, [props.lineChart.data]);

  const handleWheel = (event: React.WheelEvent<HTMLElement>): void => {
    const { deltaY } = event;
    const zoomStep = 1;
    const [start, end] = domain;

    if (deltaY > 0) {
      // zoom out
      setDomain([
        Math.max(0, start - zoomStep),
        Math.min(props.lineChart.data.length - 1, end + zoomStep),
      ]);
    } else if (end - start > 1) {
      if (start + zoomStep !== end - zoomStep) {
        // zoom in
        setDomain([
          Math.min(props.lineChart.data.length - 1, start + zoomStep),
          Math.max(0, end - zoomStep),
        ]);
      }
    }
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>): void => {
    setStartX(event.clientX);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLElement>): void => {
    const panStep = 0.2;

    if (startX !== null) {
      const { movementX } = event;

      if (movementX > 0 && domain[0] > 0 && domain[1] > 0 && domain[0] < domain[1]) {
        // move left
        setDomain([domain[0] - panStep, domain[1] - panStep]);
      } else if (
        movementX < 0 &&
        domain[0] < props.lineChart.data.length - 1 &&
        domain[1] < props.lineChart.data.length - 1 &&
        domain[0] < domain[1]
      ) {
        // move right
        setDomain([domain[0] + panStep, domain[1] + panStep]);
      }

      setStartX(event.clientX);
    }
  };

  const handleMouseUp = (): void => {
    setStartX(null);
  };

  return (
    <div
      className="h-full"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      ref={chartWrapperRef}
    >
      <LineChart
        {...props}
        xAxis={{
          domain:
            props.xAxis.dataKey &&
            props.lineChart.data.length > 1 &&
            domain[1] < props.lineChart.data.length
              ? [
                  props.lineChart.data[Math.round(domain[0])][props.xAxis.dataKey.toString()],
                  props.lineChart.data[Math.round(domain[1])][props.xAxis.dataKey.toString()],
                ]
              : [],
          type: 'number',
          allowDataOverflow: true,
          tickCount: 10,
          ...props.xAxis,
        }}
      />
    </div>
  );
};

interface InteractiveLineChartProps {
  container: Omit<ResponsiveContainerProps, 'children'>;
  grid: Omit<CartesianGridProps, 'ref'>;
  tooltip?: TooltipProps<ValueType, NameType>;
  xAxis: Omit<XAxisProps, 'type' | 'tickCount'>;
  yAxis: YAxisProps;
  referenceLines?: ReferenceLineProps[];
  lineChart: {
    width?: number;
    height?: number;
    data: object[];
    margin?: {
      top?: number;
      right?: number;
      bottom?: number;
      left?: number;
    };
  };
  lines?: Omit<LineProps, 'ref'>[];
}

export default InteractiveLineChart;
